/**
 * Path <-> page mapping, kept out of `App.tsx` for the same reason `navigation.ts` is split
 * from `router.tsx` (D41): a file exporting both components and plain functions/constants
 * defeats React Fast Refresh, which can no longer hot-swap it without a full reload.
 *
 * `RedirectToSettings` lives here too, unexported — it is genuinely a component (it calls
 * `useEffect`), but nothing outside `FIXED_ROUTES` ever needs to name it, and an unexported
 * component does not trip the same Fast-Refresh rule that made this file necessary in the
 * first place. It needs no JSX (`return null`), so this can stay a plain `.ts` file.
 */

import { useEffect } from "react";
import { BlackjackPage } from "./pages/BlackjackPage";
import { Structure } from "./pages/Structure";
import { Settings } from "./pages/Settings";
import { Models } from "./pages/Models";
import { Briefing } from "./pages/Briefing";
import { navigate } from "./navigation";
import type { PageId } from "./layout/model";

/** Where the built-in pages have always lived. Everything else is `/page/:id`. */
export const BUILTIN_PATHS: Record<string, string> = {
  briefing: "/",
  structure: "/settings/structure",
  settings: "/settings",
  models: "/models",
};

export function pathForPage(id: PageId): string {
  return BUILTIN_PATHS[id] ?? `/page/${id}`;
}

const PAGE_ROUTE = /^\/page\/([^/]+)$/;

function RedirectToSettings(): null {
  useEffect(() => navigate("/settings", { replace: true }), []);
  return null;
}

const FIXED_ROUTES: Record<string, () => React.ReactElement | null> = {
  "/": Briefing,
  "/blackjack": BlackjackPage,
  "/settings/structure": Structure,
  "/settings": Settings,
  "/settings/tokens": RedirectToSettings,
  "/models": Models,
};

/**
 * What to render for a path, as data rather than a component reference — a `/page/:id`
 * match returns the id, not a `() => <GenericPage id={id} />` closure. That closure would be
 * a fresh function identity on every single render of `App` (not just on navigation), and
 * React remounts a component whose reference changed identity — losing every card's state
 * on the current custom page any time `App` re-rendered for an unrelated reason (renaming a
 * page elsewhere in the nav, for instance). Returning the id and letting `App.tsx` render
 * `<GenericPage id={id} />` directly keeps `GenericPage` itself as the stable reference;
 * only its `id` prop changes, which is an update, not a remount.
 *
 * Briefing, Structure and Settings keep their original fixed paths even though they are
 * now three entries in the same editable page list stage 4 introduced (`layout/model.ts`)
 * — an old bookmark, and `/` as the app's home route, both have to keep meaning what they
 * already mean. A page a reader creates gets `/page/:id` instead, since it never had an
 * established path to preserve.
 */
export type Route =
  | { kind: "fixed"; Component: () => React.ReactElement | null }
  | { kind: "generic"; id: string }
  | { kind: "not-found" };

export function routeFor(path: string): Route {
  const fixed = FIXED_ROUTES[path];
  if (fixed) return { kind: "fixed", Component: fixed };
  const match = PAGE_ROUTE.exec(path);
  if (match) return { kind: "generic", id: match[1] };
  return { kind: "not-found" };
}
